import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  useMediaQuery,
  Chip,
  useTheme,
  Dialog,
  DialogContent,
  Skeleton,
  Avatar
} from '@mui/material';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import QuizIcon from '@mui/icons-material/Quiz';
import LW_Embeddable from '../integrations/LW_Agent_Conversational';
import ProductFAQList from '../product/ProductFAQList';
import ProductResources from '../product/ProductResources';
import EmbeddedComponentError from '../error/EmbeddedComponentError';
import ErrorBoundary from '../error/ErrorBoundary';
import Breadcrumb from '../common/Breadcrumb';
import { search } from '@api/search';
import { formatPrice, generatePlaceholderImageUrl } from '@utils/formatter';
import { loadCart, saveCart } from '@utils/cartUtils';
import { logError } from '@utils/errorLogger';

// Custom hooks
const useProduct = (id) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const searchConfigs = [
          { query: `id:"${id}"`, queryProfile: 'pdp' },
          { query: `unique_id:"${id}"`, queryProfile: 'default' },
          { query: `product_id:"${id}"`, queryProfile: 'default' }
        ];

        const fields = 'id,title,price,price_d,price_f,image_url_s,image_url,image,image_url_t,image_urls,images,description,manufacturer,category,sku,variant_images,variants,total_variants,group,group_s,unique_id,product_id,primary_color_s';
        
        let productData = null;
        let result = null;

        for (const config of searchConfigs) {
          result = await search({
            ...config,
            rows: 1,
            collapse: true,
            collapseField: 'group',
            expand: true,
            expandField: 'image_url',
            fields
          });
          
          if (result.success && result.documents?.length > 0) {
            productData = result.documents[0];
            break;
          }
        }

        if (!productData) {
          throw new Error('Product not found');
        }

        // Process variants
        const expanded = result.data?.expanded || {};
        const groupId = productData.group || productData.id;
        let variantData = expanded[groupId]?.docs || [];

        if (variantData.length === 0 && productData.group) {
          const groupResult = await search({
            query: `group:"${productData.group}"`,
            rows: 20,
            queryProfile: 'default',
            collapse: true,
            collapseField: 'group',
            expand: true,
            expandField: 'image_url',
            fields
          });

          if (groupResult.success) {
            const expandedGroup = groupResult.data?.expanded || {};
            variantData = expandedGroup[productData.group]?.docs || [];
          }
        }

        // Process variant data
        const processedVariants = variantData
          .filter(doc => doc.image_url)
          .map(doc => ({
            image_url: doc.image_url,
            sku: doc.sku || doc.id,
            title: doc.title,
            id: doc.id,
            product_id: doc.product_id,
            primary_color_s: doc.primary_color_s,
          }));

        const mainVariant = {
          image_url: productData.image_url,
          sku: productData.sku || productData.id,
          title: productData.title,
          id: productData.id,
          product_id: productData.product_id,
          primary_color_s: productData.primary_color_s,
        };

        if (productData.image_url && !processedVariants.some(v => v.image_url === productData.image_url)) {
          processedVariants.unshift(mainVariant);
        }

        setProduct({
          ...productData,
          variant_images: processedVariants.map(v => v.image_url),
          variant_data: processedVariants.length > 0 ? processedVariants : [mainVariant],
        });

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  return { product, loading, error };
};

const useProductImages = (product) => {
  return useMemo(() => {
    if (!product) return { mainImage: '', variantImages: [] };

    const productTitle = product.title || 'Unnamed Product';
    const mainImage = product.image_url_s || 
                     product.image_url || 
                     product.image || 
                     generatePlaceholderImageUrl(productTitle);

    let images = [];
    if (product.variant_images?.length > 0) {
      images = product.variant_images;
    } else {
      // Collect all possible image fields
      const imageFields = [
        product.image_url_s,
        product.image_url,
        product.image,
        product.image_url_t,
        ...(product.image_urls || []),
        ...(product.images || [])
      ];
      
      // Add variant images
      if (product.variants?.length > 0) {
        product.variants.forEach(variant => {
          imageFields.push(
            variant.image_url_s,
            variant.image_url,
            variant.image,
            variant.image_url_t,
            ...(variant.images || [])
          );
        });
      }
      
      images = [...new Set(imageFields.filter(img => img && img.trim()))];
    }

    return {
      mainImage,
      variantImages: images.length > 0 ? images : [mainImage]
    };
  }, [product]);
};

// Skeleton component
function ProductDetailSkeleton() {
  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      <Skeleton variant='text' height={24} width='60%' sx={{ mb: 3 }} />
      
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Grid container>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Skeleton variant='rectangular' height={400} sx={{ borderRadius: 2 }} />
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ p: 2 }}>
              <Skeleton variant='text' height={32} width='85%' sx={{ mb: 1 }} />
              <Skeleton variant='text' height={20} width='40%' sx={{ mb: 2 }} />
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} variant='text' height={16} width={`${100 - i * 5}%`} sx={{ mb: 0.5 }} />
              ))}
              
              <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Skeleton variant='text' height={48} width='40%' sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant='rectangular' width={70} height={40} />
                  <Skeleton variant='rectangular' width='100%' height={48} />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ p: 2, minHeight: 600 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Skeleton variant='rectangular' height={600} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant='rectangular' height={600} sx={{ borderRadius: 2 }} />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

// Main component
export default function ProductDetail() {
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { product, loading, error } = useProduct(id);
  const { mainImage, variantImages } = useProductImages(product);
  
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const query = new URLSearchParams(location.search).get('q') || '*:*';
  const currentImage = variantImages[selectedImage] || variantImages[0] || mainImage;
  const price = product?.price || product?.price_d || product?.price_f || 0;
  const formattedPrice = formatPrice(price);

  const breadcrumbItems = useMemo(() => [
    { label: 'Home', href: '/' },
    ...(product?.category ? [{ 
      label: product.category, 
      href: `/search?category=${encodeURIComponent(product.category)}&q=${encodeURIComponent(query)}` 
    }] : []),
    ...(product?.manufacturer ? [{ 
      label: product.manufacturer, 
      href: `/search?manufacturer=${encodeURIComponent(product.manufacturer)}&q=${encodeURIComponent(query)}` 
    }] : []),
  ], [product, query]);

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const handleQuantityChange = useCallback((e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) setQuantity(value);
  }, []);

  const addToCart = useCallback(() => {
    try {
      const cart = loadCart() || [];
      let productToAdd = { ...product };

      if (variantImages.length > 1 && product.variant_data?.[selectedImage]) {
        const selectedVariant = product.variant_data[selectedImage];
        productToAdd = {
          ...product,
          id: selectedVariant.id || product.id,
          sku: selectedVariant.sku,
          title: selectedVariant.title || product.title,
          image_url: selectedVariant.image_url,
          variant_info: {
            selected_sku: selectedVariant.sku,
            selected_image: selectedVariant.image_url,
            parent_id: product.id,
            variant_index: selectedImage,
          },
        };
      }

      const cartItemId = productToAdd.variant_info
        ? `${product.id}-${productToAdd.variant_info.selected_sku}`
        : productToAdd.id;

      const existingIndex = cart.findIndex(item => item.cart_id === cartItemId);

      if (existingIndex >= 0) {
        cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + quantity;
      } else {
        cart.push({
          ...productToAdd,
          cart_id: cartItemId,
          quantity,
          image: productToAdd.image_url || productToAdd.image_url_s || mainImage,
          category: productToAdd.category,
        });
      }

      saveCart(cart);
      const variantText = productToAdd.variant_info ? ` (${productToAdd.variant_info.selected_sku})` : '';
      showSnackbar(`${productToAdd.title}${variantText} added to cart!`);
    } catch {
      showSnackbar('Failed to add item to cart');
    }
  }, [product, selectedImage, quantity, variantImages.length, mainImage, showSnackbar]);

  if (loading) return <ProductDetailSkeleton />;
  
  if (error || !product) {
    return (
      <Container maxWidth='lg' sx={{ py: 8 }}>
        <Typography color='error' align='center'>
          {error || 'Product not found'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumb items={breadcrumbItems} />
      </Box>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Product Overview */}
        <Grid container>
          {/* Product Image */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Box
                onClick={() => setImageModalOpen(true)}
                sx={{
                  aspectRatio: '1/1',
                  maxHeight: 400,
                  bgcolor: 'white',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover .zoom-hint': { opacity: 1 }
                }}
              >
                <Box
                  component='img'
                  src={currentImage}
                  alt={product.title}
                  sx={{
                    maxHeight: '90%',
                    maxWidth: '90%',
                    objectFit: 'contain',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                  onError={(e) => {
                    e.target.src = mainImage;
                  }}
                />
                <Chip
                  label='Click to enlarge'
                  size='small'
                  className='zoom-hint'
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    bgcolor: 'rgba(0,0,0,0.8)',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={8}>
            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Title and Description */}
              <Box sx={{ mb: 2 }}>
                <Typography variant='h5' component='h1' sx={{ fontWeight: 500, mb: 1 }}>
                  {product.title}
                </Typography>

                {product.attribute_at_collection_ss && (
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                    from the{' '}
                    <Link
                      to={`/?q=${encodeURIComponent(product.attribute_at_collection_ss)}`}
                      style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}
                    >
                      {product.attribute_at_collection_ss} Collection
                    </Link>
                  </Typography>
                )}

                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.6,
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {product.description || 'No detailed description available for this product.'}
                </Typography>

                {/* Variant Selection */}
                {variantImages.length > 1 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                      Select Option:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {variantImages.map((img, idx) => {
                        const variantInfo = product.variant_data?.[idx] || { sku: `Option ${idx + 1}` };
                        const isSelected = selectedImage === idx;

                        return (
                          <Box
                            key={idx}
                            onClick={() => setSelectedImage(idx)}
                            sx={{
                              cursor: 'pointer',
                              border: isSelected ? 2 : 1,
                              borderColor: isSelected ? 'primary.main' : 'rgba(0,0,0,0.23)',
                              borderRadius: 1.5,
                              p: 1,
                              bgcolor: isSelected ? 'primary.50' : 'white',
                              minWidth: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              transition: 'all 0.2s',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                borderColor: 'primary.main',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              }
                            }}
                          >
                            <Box
                              component='img'
                              src={img}
                              alt={variantInfo.primary_color_s || variantInfo.sku}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'contain',
                                bgcolor: '#f9f9f9',
                                borderRadius: 1
                              }}
                              onError={(e) => {
                                e.target.src = mainImage;
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Purchase Section */}
              <Box sx={{ mt: 'auto', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}
                >
                  {formattedPrice}
                </Typography>
                
                <Typography
                  variant='body2'
                  color='success.main'
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  ✓ In Stock • FREE delivery
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Qty:
                  </Typography>
                  <TextField
                    select
                    value={quantity}
                    onChange={handleQuantityChange}
                    SelectProps={{ native: true }}
                    size='small'
                    sx={{ minWidth: 70 }}
                  >
                    {[...Array(10)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </TextField>

                  <Button
                    variant='contained'
                    onClick={addToCart}
                    className='lw-cart-add-trigger'
                    sx={{
                      flex: 1,
                      bgcolor: '#C99063',
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#B8804F' }
                    }}
                  >
                    Add to Cart
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Information Section */}
        {product._lw_ai_faq_enrichment_t && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Grid container spacing={2}>
              {/* FAQs and Resources */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  minHeight: 600, 
                  display: 'flex', 
                  flexDirection: 'column' 
                }}>
                  {/* FAQs */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#537E87' }}>
                      <QuizIcon fontSize="small" />
                    </Avatar>
                    <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      Frequently Asked Questions
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <ProductFAQList faqData={product._lw_ai_faq_enrichment_t} />
                    
                    {/* Resources */}
                    {product._lw_has_pdfs !== false && product._lw_pdf_title_ss && (
                      <Box sx={{ mt: 3 }}>
                        <ProductResources product={product} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Q&A Bot */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  minHeight: 600,
                  borderRadius: 2, 
                  bgcolor: 'white'
                }}>
                  <ErrorBoundary
                    componentName='QnAWidget'
                    fallbackComponent={(props) => (
                      <EmbeddedComponentError
                        {...props}
                        title='Q&A Widget Failed to Load'
                        message='Unable to load the Q&A widget. Please try again later.'
                        minHeight='400px'
                      />
                    )}
                    onError={(error, info) =>
                      logError(error, {
                        componentName: 'QnAWidget',
                        productId: product?.id,
                        componentStack: info?.componentStack,
                      })
                    }
                  >
                    <LW_Embeddable
                      id='qna-template-1'
                      mockMode={true}
                      productId={product?.id || product?.unique_id || '1251002'}
                      product={product}
                    />
                  </ErrorBoundary>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Mobile Cart Button */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            p: 1.5,
            zIndex: 1000,
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant='h6' color='error.main' sx={{ fontWeight: 600 }}>
            {formattedPrice}
          </Typography>
          <Button
            variant='contained'
            onClick={addToCart}
            className='lw-cart-add-trigger'
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Add to Cart
          </Button>
        </Paper>
      )}

      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
      >
        <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            {product.variant_data?.[selectedImage] && (
              <Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main' }}>
                Color: {product.variant_data[selectedImage].primary_color_s || product.variant_data[selectedImage].sku}
              </Typography>
            )}
            <IconButton onClick={() => setImageModalOpen(false)}>
              <Typography variant='h6'>×</Typography>
            </IconButton>
          </Box>

          {/* Main Image */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <img
              src={variantImages[selectedImage] || mainImage}
              alt={product.title}
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />

            {/* Navigation */}
            {variantImages.length > 1 && (
              <>
                <IconButton
                  sx={{
                    position: 'absolute',
                    left: 16,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                  onClick={() => setSelectedImage(prev => prev > 0 ? prev - 1 : variantImages.length - 1)}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: 16,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                  onClick={() => setSelectedImage(prev => prev < variantImages.length - 1 ? prev + 1 : 0)}
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}
          </Box>

          {/* Thumbnails */}
          {variantImages.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1.5, p: 2, justifyContent: 'center', overflowX: 'auto' }}>
              {variantImages.map((img, idx) => (
                <Box
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  sx={{
                    width: 70,
                    height: 70,
                    border: selectedImage === idx ? '2px solid' : '1px solid',
                    borderColor: selectedImage === idx ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                >
                  <img
                    src={img}
                    alt={`Variant ${idx + 1}`}
                    style={{ maxHeight: '90%', maxWidth: '90%', objectFit: 'contain' }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity='success'
          variant='filled'
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}